import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageArea from '../shared/PageArea';
import PageTitle from '../shared/PageTitle';
import LoadingWheel from '../shared/LoadingWheel';
import { StatusChip, confidenceTone, fieldStatusTone, formatConfidence } from '../shared/StatusChip';
import { DocumentFieldsResponse, DocumentsClient, ExtractedFieldItem, FieldHistoryItem, ProcessingClient } from '../../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';
import DocumentPreview from './DocumentPreview';
import TypedFieldInput, { initialFieldValue } from './TypedFieldInput';
import { toDateOnly } from './dateOnly';
import { etagFromRowVersion, isStaleEditStatus } from './reviewHeaders';

const ReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { documentId } = useParams<{ documentId: string }>();
  const client = useMemo(() => new ProcessingClient(getApiBaseUrl(), authenticatedFetch), []);
  const documentsClient = useMemo(() => new DocumentsClient(getApiBaseUrl(), authenticatedFetch), []);

  const [data, setData] = useState<DocumentFieldsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState<ExtractedFieldItem | null>(null);
  const [newValue, setNewValue] = useState('');
  const [adding, setAdding] = useState(false);
  const [definitionId, setDefinitionId] = useState('');
  const [manualValue, setManualValue] = useState('');
  const [qualifier, setQualifier] = useState('');
  const [manualReason, setManualReason] = useState('');
  const [pageNumber, setPageNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [entryError, setEntryError] = useState<string | null>(null);
  const selectedDefinition = data?.availableFields?.find(d => d.id === definitionId);
  const [reason, setReason] = useState('');
  const [overrideDate, setOverrideDate] = useState('');
  const [history, setHistory] = useState<FieldHistoryItem[] | null>(null);
  const [verificationToConfirm, setVerificationToConfirm] = useState<
    { type: 'field'; field: ExtractedFieldItem } | { type: 'document' } | null
  >(null);
  const [searchParams] = useSearchParams();
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(searchParams.get('field'));
  useEffect(() => setSelectedFieldId(searchParams.get('field')), [searchParams, documentId]);

  const load = useCallback((refresh = false) => {
    if (!documentId) {
      return;
    }

    if (!refresh) setLoading(true);
    client
      .getApiDocumentsFields(documentId)
      .then(setData)
      .catch(() => {
        if (!refresh) setData(null);
        else setMessage(t('common.error'));
      })
      .finally(() => setLoading(false));
  }, [client, documentId, t]);

  useEffect(() => load(), [load]);

  const downloadDocument = async () => {
    if (!documentId) {
      return;
    }

    try {
      const content = await documentsClient.getApiDocumentsContent(documentId);
      if (content.downloadUrl) {
        window.open(content.downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch {
      setMessage(t('common.error'));
    }
  };

  const saveCorrection = async () => {
    if (!correcting?.id) {
      return;
    }

    try {
      const ifMatch = etagFromRowVersion(correcting.rowVersion);
      const response = await authenticatedFetch.fetch(
        `${getApiBaseUrl() ?? ''}/api/fields/${encodeURIComponent(correcting.id)}/correct`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(ifMatch ? { 'If-Match': ifMatch } : {}),
          },
          body: JSON.stringify({
            newRawValue: newValue || undefined,
            overrideDate: toDateOnly(overrideDate),
            reason: reason || undefined,
          }),
        },
      );
      if (isStaleEditStatus(response.status)) {
        setMessage(t('reviewPage.staleEdit'));
        setCorrecting(null);
        load(true);
        return;
      }
      if (!response.ok) {
        throw new Error(`Correction request failed (${response.status})`);
      }
      setMessage(t('reviewPage.corrected'));
      setCorrecting(null);
      load(true);
    } catch {
      setMessage(t('common.error'));
    }
  };

  const addField = async () => {
    if (!documentId || !definitionId) return;
    setSaving(true);
    setEntryError(null);
    try {
      const response = await authenticatedFetch.fetch(`${getApiBaseUrl() ?? ''}/api/documents/${encodeURIComponent(documentId)}/fields`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fieldDefinitionId: definitionId, value: manualValue, qualifier: qualifier || undefined,
          pageNumber: pageNumber ? Number(pageNumber) : undefined, reason: manualReason }),
      });
      if (!response.ok) {
        const problem = await response.json();
        throw new Error(problem.detail ?? problem.title ?? t('common.error'));
      }
      setAdding(false);
      setMessage(t('reviewPage.fieldAdded'));
      load(true);
    } catch (error) {
      setEntryError(error instanceof Error ? error.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const verifyOne = async (field: ExtractedFieldItem) => {
    if (!field.id) {
      return;
    }
    try {
      const ifMatch = etagFromRowVersion(field.rowVersion);
      const response = await authenticatedFetch.fetch(
        `${getApiBaseUrl() ?? ''}/api/fields/${encodeURIComponent(field.id)}/verify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(ifMatch ? { 'If-Match': ifMatch } : {}),
          },
          body: JSON.stringify({}),
        },
      );
      if (isStaleEditStatus(response.status)) {
        setMessage(t('reviewPage.staleEdit'));
        load(true);
        return;
      }
      if (!response.ok) {
        throw new Error(`Verify request failed (${response.status})`);
      }
      load(true);
    } catch {
      setMessage(t('common.error'));
    }
  };

  const verifyAll = async () => {
    if (!documentId) {
      return;
    }

    try {
      const result = await client.postApiFieldsBulkVerify({ documentId });
      setMessage(t('projectSummary.verified', { count: result.verifiedCount ?? 0 }));
      load(true);
    } catch {
      setMessage(t('common.error'));
    }
  };

  const confirmVerification = () => {
    const confirmation = verificationToConfirm;
    setVerificationToConfirm(null);
    if (confirmation?.type === 'field') {
      void verifyOne(confirmation.field);
    } else if (confirmation?.type === 'document') {
      void verifyAll();
    }
  };

  const openHistory = async (field: ExtractedFieldItem) => {
    if (!field.id) {
      return;
    }
    const items = await client.getApiFieldsHistory(field.id);
    setHistory(items);
  };

  if (loading) {
    return (
      <PageArea>
        <LoadingWheel />
      </PageArea>
    );
  }

  if (!data) {
    return (
      <PageArea>
        <Typography color="text.secondary">{t('common.noData')}</Typography>
      </PageArea>
    );
  }

  const fields = data.fields ?? [];
  const hasUnresolvedAmbiguousDate = fields.some(
    (field) => field.dateAmbiguous && field.status !== 'Verified',
  );

  return (
    <PageArea>
      <Button startIcon={<ArrowBackIcon />} size="md" onClick={() => navigate(-1)} sx={{ mb: 1 }}>
        {t('common.back')}
      </Button>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <PageTitle
          title={`${t('reviewPage.title')} - ${data.fileName}`}
          subtitle={[data.projectReference, data.vesselName, data.documentTypeCode, data.status].filter(Boolean).join(' · ')}
        />
        <Button
          variant="primary"
          size="md"
          onClick={() => setVerificationToConfirm({ type: 'document' })}
          disabled={hasUnresolvedAmbiguousDate}
          sx={{ minWidth: 180, px: '20px', flexShrink: 0 }}
        >
          {t('reviewPage.verifyAll')}
        </Button>
      </Stack>
      {data.projectId && (
        <Button size="md" onClick={() => navigate(`/projects/${data.projectId}`)} sx={{ mb: 2 }}>
          {[data.projectReference, data.vesselName].filter(Boolean).join(' · ')}
        </Button>
      )}
      {message && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(280px, 1fr) 1.4fr' },
          alignItems: 'start',
        }}
      >
        <Paper variant="outlined" sx={{ minHeight: 320, borderRadius: 2, overflow: 'hidden', position: { lg: 'sticky' }, top: 16 }}>
          {documentId && (
            <DocumentPreview
              documentId={documentId}
              fileName={data.fileName}
              selectedFieldId={selectedFieldId}
              onSelectField={setSelectedFieldId}
              onDownload={downloadDocument}
              fields={fields.map((field) => ({
                id: field.id,
                pageNumber: field.pageNumber,
                boundingBox: field.boundingBox,
                label: field.displayLabel ?? field.fieldKey,
                rawValue: field.rawValue,
              }))}
            />
          )}
        </Paper>

        <Stack spacing={1}>
          <Button size="md" variant="secondary" onClick={() => {
            setAdding(true); setDefinitionId(''); setManualValue(''); setQualifier('');
            setManualReason(''); setPageNumber(''); setEntryError(null);
          }} disabled={!data.availableFields?.length}>{t('reviewPage.addField')}</Button>
          <Alert severity="info">{t('reviewPage.hint')}</Alert>
          {hasUnresolvedAmbiguousDate && (
            <Alert severity="warning">{t('reviewPage.ambiguousDateAction')}</Alert>
          )}
          {fields.length === 0 && (
            <Typography color="text.secondary">{t('reviewPage.noFields')}</Typography>
          )}
          {fields.map((f) => (
            <Paper
              key={f.id}
              variant="outlined"
              onClick={() => f.id && setSelectedFieldId(f.id)}
              sx={{
                p: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                borderColor: f.id === selectedFieldId ? 'primary.main' : undefined,
                boxShadow: f.id === selectedFieldId ? 2 : 0,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Typography variant="textSM" color="text.secondary">
                    {f.displayLabel ?? f.fieldKey}
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {f.rawValue}
                    {f.pageNumber != null && (
                      <Typography component="span" color="text.secondary" sx={{ ml: 1, fontWeight: 400, fontSize: 12 }}>
                        p.{f.pageNumber}
                      </Typography>
                    )}
                  </Typography>
                </Box>
                <StatusChip label={formatConfidence(f.confidence)} tone={confidenceTone(f.confidence)} />
                <StatusChip label={f.status ?? ''} tone={fieldStatusTone(f.status)} />
                {f.dateAmbiguous && <StatusChip label={t('reviewPage.dateAmbiguous')} tone="warning" />}
                {f.autoVerified && <StatusChip label={t('reviewPage.autoVerified')} tone="success" />}
                <Button
                  size="md"
                  onClick={(event) => {
                    event.stopPropagation();
                    void openHistory(f);
                  }}
                  aria-label={t('reviewPage.history')}
                >
                  <HistoryIcon fontSize="small" />
                </Button>
                {f.status !== 'Verified' && (
                  <Button
                    size="md"
                    disabled={Boolean(f.dateAmbiguous)}
                    onClick={(event) => {
                      event.stopPropagation();
                      setVerificationToConfirm({ type: 'field', field: f });
                    }}
                  >
                    {t('reviewPage.verify')}
                  </Button>
                )}
                <Button
                  size="md"
                  variant="secondary"
                  onClick={(event) => {
                    event.stopPropagation();
                    setCorrecting(f);
                    setNewValue(initialFieldValue(f));
                    setReason('');
                    setOverrideDate('');
                  }}
                >
                  {t('common.edit')}
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Box>

      <Dialog open={Boolean(correcting)} onClose={() => setCorrecting(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('reviewPage.saveCorrection')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TypedFieldInput definition={data.availableFields?.find(d => d.id === correcting?.fieldDefinitionId)}
              dataType={correcting?.dataType} value={newValue} onChange={setNewValue} />
            <TextField
              label={t('reviewPage.overrideDate')}
              type="date"
              InputLabelProps={{ shrink: true }}
              value={overrideDate}
              onChange={(e) => setOverrideDate(e.target.value)}
              required={Boolean(correcting?.dateAmbiguous)}
              helperText={correcting?.dateAmbiguous ? t('reviewPage.overrideDateRequired') : undefined}
              fullWidth
            />
            <TextField label={t('reviewPage.reason')} value={reason} onChange={(e) => setReason(e.target.value)} fullWidth multiline />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="md" onClick={() => setCorrecting(null)}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            size="md"
            onClick={saveCorrection}
            disabled={Boolean(correcting?.dateAmbiguous && !toDateOnly(overrideDate))}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={adding} onClose={() => !saving && setAdding(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('reviewPage.addField')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">{t('reviewPage.manualEntryHint')}</Alert>
            {entryError && <Alert severity="error">{entryError}</Alert>}
            <TextField select label={t('reviewPage.fieldType')} value={definitionId} onChange={e => {
              setDefinitionId(e.target.value); setManualValue('');
            }} fullWidth required>
              {(data.availableFields ?? []).map(d => <MenuItem key={d.id} value={d.id}>
                {d.displayLabel} ({d.documentTypeCode} · {d.dataType})
              </MenuItem>)}
            </TextField>
            {selectedDefinition && <TypedFieldInput definition={selectedDefinition} value={manualValue} onChange={setManualValue} />}
            <TextField label={t('reviewPage.qualifier')} value={qualifier} onChange={e => setQualifier(e.target.value)}
              helperText={t('reviewPage.qualifierHint')} inputProps={{ maxLength: 200 }} fullWidth />
            <TextField label={t('reviewPage.sourcePage')} type="number" value={pageNumber}
              onChange={e => setPageNumber(e.target.value)} inputProps={{ min: 1, step: 1 }} fullWidth />
            <TextField label={t('reviewPage.reason')} value={manualReason} onChange={e => setManualReason(e.target.value)}
              inputProps={{ maxLength: 1000 }} fullWidth required multiline />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="md" disabled={saving} onClick={() => setAdding(false)}>{t('common.cancel')}</Button>
          <Button size="md" variant="primary" onClick={addField}
            disabled={saving || !definitionId || !manualValue.trim() || !manualReason.trim()}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={history !== null} onClose={() => setHistory(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('reviewPage.history')}</DialogTitle>
        <DialogContent>
          {(history ?? []).length === 0 ? (
            <Typography color="text.secondary">{t('common.noData')}</Typography>
          ) : (
            (history ?? []).map((h) => (
              <Box key={h.id} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Typography sx={{ fontWeight: 600 }}>{h.action}</Typography>
                <Typography variant="textSM">
                  {h.previousRawValue} → {h.newRawValue}
                </Typography>
                {h.reason && (
                  <Typography variant="textSM" color="text.secondary">
                    {h.reason}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={verificationToConfirm !== null} onClose={() => setVerificationToConfirm(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('reviewPage.confirmVerifyTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {verificationToConfirm?.type === 'field'
              ? t('reviewPage.confirmVerifyField', {
                  field: verificationToConfirm.field.displayLabel ?? verificationToConfirm.field.fieldKey,
                })
              : t('reviewPage.confirmVerifyDocument', { document: data.fileName })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="md" onClick={() => setVerificationToConfirm(null)}>{t('common.cancel')}</Button>
          <Button variant="primary" size="md" onClick={confirmVerification}>
            {t('reviewPage.verify')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default ReviewPage;
